/**
 * `/batches/labels` — the sheet, as a URL (PLAN-LABELS C2, G3, D-LB8).
 *
 * One route for both halves of the job: the brewer taps the sheet out here and
 * this is what prints (D-LB8). So the URL is not just where a finished run was
 * sent — it is the sketch itself, rewritten on every tap, which is why each
 * write below is a `replace`. Thirty taps should leave one entry in the
 * history, not thirty, and Back should be the way out of the page rather than
 * a way of undoing the last label.
 *
 * What that buys is everything the owner asked for: a reload keeps the sheet,
 * a bookmark keeps it, and the link sent to the machine the label stock is
 * loaded in opens the same half-tapped sheet on the other side of the house.
 *
 * `validateSearch` is `parseLabelRun` followed by `labelRunSearch`, which is
 * not a round trip for its own sake: parsing drops the ids and cell numbers
 * that do not make sense and re-serialising writes back only what survived, so
 * a hand-edited query is tidied in the address bar rather than carried around
 * half-broken. `lib/label-run.ts` explains why both the string and the array
 * shape of `labels` have to be read — the router writes back what comes *out*
 * of this function, and the tasting cards route has the story of what that cost
 * the first time it was missed.
 */

import type { SearchSchemaInput } from '@tanstack/react-router';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { Labels } from '@/components/batches/labels';
import { type LabelRunSearch, labelRunSearch, parseLabelRun } from '@/lib/label-run';
import { useReturn } from '@/lib/return-ticket';

/**
 * `?recipe=` as the one flag this page carries, and the tasting cards' flag
 * exactly (D-LB5).
 *
 * Written out again rather than imported: it is six tokens of the tasting cards
 * route's own private parsing, and lifting it into a shared module to save
 * those six would make two pages' URL grammar one thing to change in step. The
 * three spellings and the reason for each are documented there, at
 * `routes/batches/tasting-cards.tsx`; if that list ever grows, this one grows
 * with it, because a label's code and a card's code open the same form.
 */
function parseRecipeFlag(value: unknown): true | undefined {
  return value === true || value === '1' || value === 'true' ? true : undefined;
}

export const Route = createFileRoute('/batches/labels')({
  /**
   * `SearchSchemaInput` on the parameter is what tells the router that what
   * goes *in* is not what comes out: the batch page arrives here with nothing
   * but `beers=<id>`, and everything reading the search back gets a run already
   * checked against the sheet.
   */
  validateSearch: (
    search: {
      readonly beers?: unknown;
      readonly labels?: unknown;
      readonly sheet?: unknown;
      readonly recipe?: unknown;
    } & SearchSchemaInput,
  ): LabelRunSearch => labelRunSearch(parseLabelRun(search), parseRecipeFlag(search.recipe)),
  component: LabelsPage,
});

function LabelsPage() {
  const search = Route.useSearch();
  // Scoped, like the tasting cards: an unscoped `useNavigate` types `search`
  // against every registered route at once and cannot know this one's shape.
  const navigate = useNavigate({ from: Route.fullPath });
  // The run holds a `Map`, so a fresh one on every render would be a fresh prop
  // on every render — and here that matters rather than being tidiness, because
  // the page mints off the beers it is handed and a new array each render would
  // be a new effect each render.
  const run = useMemo(() => parseLabelRun(search), [search]);
  // Labels are reached from the batch page, the blend page or (D-JL7) the
  // journal page, each itself reached from its list, so "the way out" is the
  // entry the brewer actually left (D-LS7) and the ticket decides it in every
  // case. A sheet opened from
  // a bookmark or a shared link has no such entry and pushes a list instead —
  // which list is the sheet's to say, because the ids in the URL are opaque
  // and only the page has resolved them (D-BS9).
  const exitToBatches = useReturn(() => {
    void navigate({ to: '/batches' });
  });
  const exitToBlending = useReturn(() => {
    void navigate({ to: '/blending' });
  });
  // And the journal page, for a sheet of journals (D-JL6): the same ticket, and
  // the journal list as the cold arrival's fallback.
  const exitToJournals = useReturn(() => {
    void navigate({ to: '/journals' });
  });
  return (
    <Labels
      run={run}
      recipe={search.recipe === true}
      // Every tap, replacing rather than pushing. @see the header
      onRunChange={(next) => {
        void navigate({
          replace: true,
          // The router scrolls to the top on every navigation unless told
          // not to, and a tap on label 27 is not a navigation the brewer
          // made: the page must stay exactly where their thumb is.
          resetScroll: false,
          search: (prev) => labelRunSearch(next, prev.recipe),
        });
      }}
      // A replace for the same reason: ticking the box is a property of the
      // sheet in front of the brewer rather than a place they went. The run
      // rides along unchanged, and re-minting with the flag flipped is exactly
      // how the codes pick the link up.
      onRecipeChange={(next) => {
        void navigate({
          replace: true,
          resetScroll: false,
          search: (prev) => ({ ...prev, recipe: next ? true : undefined }),
        });
      }}
      onBack={(list) => {
        if (list === 'blending') exitToBlending();
        else if (list === 'journals') exitToJournals();
        else exitToBatches();
      }}
    />
  );
}
